import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Euro, Clock, Package, Rocket, Save, Loader2, Plus, Trash2, Edit2,
  CheckCircle, AlertCircle, Users, Palette, Video, Globe, Smartphone,
  PenTool, Camera, FileText, Megaphone, Search, Server, Wrench
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Service categories with icons
const SERVICE_CATEGORIES = [
  { id: 'branding', label: 'Branding & Identité', icon: Palette },
  { id: 'web', label: 'Web & Digital', icon: Globe },
  { id: 'video', label: 'Vidéo & Motion', icon: Video },
  { id: 'print', label: 'Print & Édition', icon: FileText },
  { id: 'photo', label: 'Photo', icon: Camera },
  { id: 'illustration', label: 'Illustration', icon: PenTool },
  { id: 'mobile', label: 'Mobile', icon: Smartphone },
  { id: 'marketing', label: 'Marketing', icon: Megaphone },
  { id: 'seo', label: 'SEO & Référencement', icon: Search },
];

// Default COPILOTE packages
const DEFAULT_COPILOTE_PACKAGES = [
  { id: 'base', name: 'À la carte', hours: 1, hourly_rate: 120, description: 'Tarif horaire standard' },
  { id: 'pack5', name: 'Pack 5h', hours: 5, hourly_rate: 110, description: '50€ d\'économie' },
  { id: 'pack10', name: 'Pack 10h', hours: 10, hourly_rate: 100, description: '200€ d\'économie' },
  { id: 'pack15', name: 'Pack 15h', hours: 15, hourly_rate: 90, description: '450€ d\'économie' },
  { id: 'pack20', name: 'Pack 20h', hours: 20, hourly_rate: 80, description: '800€ d\'économie' },
];

// Default Hosting Plans
const DEFAULT_HOSTING_PLANS = [
  { 
    id: 'essential', 
    name: 'Essentiel', 
    monthly_price: 29, 
    storage: '5 Go',
    bandwidth: 'Illimitée',
    emails: '5 adresses',
    ssl: true,
    backup: 'Hebdomadaire',
    support: 'Email',
    description: 'Idéal pour les sites vitrines'
  },
  { 
    id: 'pro', 
    name: 'Professionnel', 
    monthly_price: 59, 
    storage: '20 Go',
    bandwidth: 'Illimitée',
    emails: '20 adresses',
    ssl: true,
    backup: 'Quotidienne',
    support: 'Email + Téléphone',
    description: 'Pour les sites e-commerce et applications',
    popular: true
  },
  { 
    id: 'business', 
    name: 'Business', 
    monthly_price: 99, 
    storage: '50 Go',
    bandwidth: 'Illimitée',
    emails: 'Illimitées',
    ssl: true,
    backup: 'Quotidienne + CDN',
    support: 'Prioritaire 24/7',
    description: 'Haute performance et disponibilité'
  },
];

// Default Maintenance Plans
const DEFAULT_MAINTENANCE_PLANS = [
  { 
    id: 'essential', 
    name: 'Essentiel', 
    monthly_price: 79, 
    hours_included: 1,
    response_time: '48h',
    updates: 'Mensuelles',
    monitoring: false,
    reports: 'Trimestriels',
    description: 'Mises à jour et support de base'
  },
  { 
    id: 'confort', 
    name: 'Confort', 
    monthly_price: 149, 
    hours_included: 2,
    response_time: '24h',
    updates: 'Bi-mensuelles',
    monitoring: true,
    reports: 'Mensuels',
    description: 'Maintenance préventive + modifications mineures',
    popular: true
  },
  { 
    id: 'premium', 
    name: 'Premium', 
    monthly_price: 249, 
    hours_included: 4,
    response_time: '4h',
    updates: 'Hebdomadaires',
    monitoring: true,
    reports: 'Hebdomadaires',
    description: 'Support prioritaire + évolutions continues'
  },
  { 
    id: 'illimite', 
    name: 'Illimité', 
    monthly_price: 499, 
    hours_included: -1,
    response_time: '1h',
    updates: 'Continue',
    monitoring: true,
    reports: 'En temps réel',
    description: 'Heures illimitées + équipe dédiée'
  },
];

const PricingSettingsPage = () => {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('copilote');
  
  // Pricing state
  const [copilotePackages, setCopilotePackages] = useState(DEFAULT_COPILOTE_PACKAGES);
  const [hostingPlans, setHostingPlans] = useState(DEFAULT_HOSTING_PLANS);
  const [maintenancePlans, setMaintenancePlans] = useState(DEFAULT_MAINTENANCE_PLANS);
  const [hourlyRates, setHourlyRates] = useState({
    creative_internal: 50,
    creative_external: 45,
    maintenance_monthly: 150,
    hosting_monthly: 30,
  });
  const [services, setServices] = useState([]);
  
  // Modals
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    category: 'web',
    description: '',
    base_price: '',
    price_type: 'fixed', // fixed, hourly, package
    estimated_hours: '',
    is_active: true
  });

  useEffect(() => {
    fetchPricingData();
  }, []);

  const fetchPricingData = async () => {
    try {
      const response = await axios.get(`${API}/settings/pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.copilote_packages) {
        setCopilotePackages(response.data.copilote_packages);
      }
      if (response.data.hosting_plans) {
        setHostingPlans(response.data.hosting_plans);
      }
      if (response.data.maintenance_plans) {
        setMaintenancePlans(response.data.maintenance_plans);
      }
      if (response.data.hourly_rates) {
        setHourlyRates(response.data.hourly_rates);
      }
      if (response.data.services) {
        setServices(response.data.services);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      // Use defaults if not found
    } finally {
      setLoading(false);
    }
  };

  const handleSavePricing = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/pricing`, {
        copilote_packages: copilotePackages,
        hosting_plans: hostingPlans,
        maintenance_plans: maintenancePlans,
        hourly_rates: hourlyRates,
        services: services
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tarification mise à jour avec succès - Synchronisation avec le site et l\'espace client effectuée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error('Error saving pricing:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateHostingPlan = (index, field, value) => {
    const updated = [...hostingPlans];
    if (field === 'monthly_price') {
      updated[index][field] = parseFloat(value) || 0;
    } else {
      updated[index][field] = value;
    }
    setHostingPlans(updated);
  };

  const updateMaintenancePlan = (index, field, value) => {
    const updated = [...maintenancePlans];
    if (field === 'monthly_price' || field === 'hours_included') {
      updated[index][field] = parseFloat(value) || 0;
    } else {
      updated[index][field] = value;
    }
    setMaintenancePlans(updated);
  };

  const updateCopilotePackage = (index, field, value) => {
    const updated = [...copilotePackages];
    updated[index][field] = field === 'hourly_rate' || field === 'hours' ? parseFloat(value) || 0 : value;
    setCopilotePackages(updated);
  };

  const handleSaveService = () => {
    if (!serviceForm.name || !serviceForm.base_price) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    
    const newService = {
      id: editingService?.id || `service-${Date.now()}`,
      ...serviceForm,
      base_price: parseFloat(serviceForm.base_price) || 0,
      estimated_hours: parseFloat(serviceForm.estimated_hours) || 0
    };
    
    if (editingService) {
      setServices(services.map(s => s.id === editingService.id ? newService : s));
    } else {
      setServices([...services, newService]);
    }
    
    setShowServiceModal(false);
    setEditingService(null);
    setServiceForm({
      name: '',
      category: 'web',
      description: '',
      base_price: '',
      price_type: 'fixed',
      estimated_hours: '',
      is_active: true
    });
    toast.success(editingService ? 'Service mis à jour' : 'Service ajouté');
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name,
      category: service.category,
      description: service.description || '',
      base_price: service.base_price.toString(),
      price_type: service.price_type || 'fixed',
      estimated_hours: service.estimated_hours?.toString() || '',
      is_active: service.is_active !== false
    });
    setShowServiceModal(true);
  };

  const handleDeleteService = (serviceId) => {
    if (window.confirm('Supprimer ce service ?')) {
      setServices(services.filter(s => s.id !== serviceId));
      toast.success('Service supprimé');
    }
  };

  const calculatePackageTotal = (pkg) => {
    return pkg.hours * pkg.hourly_rate;
  };

  const calculateSavings = (pkg) => {
    const baseRate = copilotePackages[0]?.hourly_rate || 120;
    return (baseRate - pkg.hourly_rate) * pkg.hours;
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="px-4 md:px-8 pb-6 md:pb-8 pt-24">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Tarification
              </h1>
              <p className="text-slate-500 mt-1">
                Gérez tous les tarifs de l'agence
              </p>
            </div>
            <Button 
              onClick={handleSavePricing} 
              disabled={saving}
              className="gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </Button>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-5 max-w-3xl">
              <TabsTrigger value="copilote" className="gap-2">
                <Rocket className="w-4 h-4" />
                <span className="hidden sm:inline">Copilote</span>
              </TabsTrigger>
              <TabsTrigger value="hosting" className="gap-2">
                <Server className="w-4 h-4" />
                <span className="hidden sm:inline">Hébergement</span>
              </TabsTrigger>
              <TabsTrigger value="maintenance" className="gap-2">
                <Wrench className="w-4 h-4" />
                <span className="hidden sm:inline">Maintenance</span>
              </TabsTrigger>
              <TabsTrigger value="services" className="gap-2">
                <Package className="w-4 h-4" />
                <span className="hidden sm:inline">Services</span>
              </TabsTrigger>
              <TabsTrigger value="hourly" className="gap-2">
                <Clock className="w-4 h-4" />
                <span className="hidden sm:inline">Horaires</span>
              </TabsTrigger>
            </TabsList>

            {/* COPILOTE Packages */}
            <TabsContent value="copilote" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                      <Rocket className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <CardTitle>Forfaits COPILOTE</CardTitle>
                      <CardDescription>
                        Tarification par volume d'heures pour les clients COPILOTE
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    {copilotePackages.map((pkg, index) => (
                      <div 
                        key={pkg.id}
                        className={cn(
                          "p-4 rounded-xl border-2 transition-all",
                          index === 0 
                            ? "border-slate-200 bg-slate-50" 
                            : "border-orange-200 bg-orange-50/50"
                        )}
                      >
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          {/* Package info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Input
                                value={pkg.name}
                                onChange={(e) => updateCopilotePackage(index, 'name', e.target.value)}
                                className="font-semibold text-lg border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                                placeholder="Nom du forfait"
                              />
                              {index === 0 && (
                                <Badge variant="outline" className="text-xs">Base</Badge>
                              )}
                            </div>
                            <Input
                              value={pkg.description}
                              onChange={(e) => updateCopilotePackage(index, 'description', e.target.value)}
                              className="text-sm text-slate-500 border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                              placeholder="Description"
                            />
                          </div>
                          
                          {/* Hours */}
                          <div className="w-24">
                            <Label className="text-xs text-slate-500">Heures</Label>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                value={pkg.hours}
                                onChange={(e) => updateCopilotePackage(index, 'hours', e.target.value)}
                                className="w-16 text-center"
                                min="1"
                              />
                              <span className="text-slate-500 text-sm">h</span>
                            </div>
                          </div>
                          
                          {/* Hourly rate */}
                          <div className="w-32">
                            <Label className="text-xs text-slate-500">Tarif horaire</Label>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                value={pkg.hourly_rate}
                                onChange={(e) => updateCopilotePackage(index, 'hourly_rate', e.target.value)}
                                className="w-20 text-center"
                                min="0"
                              />
                              <span className="text-slate-500 text-sm">€/h</span>
                            </div>
                          </div>
                          
                          {/* Total */}
                          <div className="w-32 text-right">
                            <Label className="text-xs text-slate-500">Total HT</Label>
                            <p className="text-xl font-bold text-slate-800">
                              {calculatePackageTotal(pkg).toLocaleString('fr-FR')}€
                            </p>
                            {index > 0 && calculateSavings(pkg) > 0 && (
                              <p className="text-xs text-green-600">
                                -{calculateSavings(pkg)}€ économisés
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pricing visualization */}
                  <div className="mt-8 p-4 bg-slate-100 rounded-xl">
                    <h4 className="font-medium mb-4">Aperçu des tarifs</h4>
                    <div className="grid grid-cols-5 gap-2">
                      {copilotePackages.map((pkg, index) => (
                        <div key={pkg.id} className="text-center">
                          <div 
                            className="mx-auto rounded-lg flex items-end justify-center mb-2"
                            style={{ 
                              width: '60px',
                              height: `${Math.max(40, (pkg.hourly_rate / 120) * 80)}px`,
                              backgroundColor: index === 0 ? '#94a3b8' : '#f97316',
                              opacity: 0.3 + (index * 0.15)
                            }}
                          >
                            <span className="text-white text-xs font-bold pb-1">{pkg.hourly_rate}€</span>
                          </div>
                          <p className="text-xs text-slate-600">{pkg.hours}h</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Hourly Rates */}
            <TabsContent value="hourly" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-cyan-600" />
                      Tarifs Créatifs
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Créatif interne (€/h)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          value={hourlyRates.creative_internal}
                          onChange={(e) => setHourlyRates({...hourlyRates, creative_internal: parseFloat(e.target.value) || 0})}
                          className="w-32"
                        />
                        <span className="text-slate-500">€ HT / heure</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Créatifs intégrés à l'équipe</p>
                    </div>
                    
                    <div>
                      <Label>Créatif externe / ponctuel (€/h)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          value={hourlyRates.creative_external}
                          onChange={(e) => setHourlyRates({...hourlyRates, creative_external: parseFloat(e.target.value) || 0})}
                          className="w-32"
                        />
                        <span className="text-slate-500">€ HT / heure</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Créatifs ponctuels en freelance</p>
                    </div>
                  </CardContent>
                </Card>

                <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                  <div className="flex items-center gap-2 text-blue-700">
                    <AlertCircle className="w-4 h-4" />
                    <p className="text-sm font-medium">Tarifs Hébergement & Maintenance</p>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    Les tarifs des contrats d'hébergement et de maintenance sont gérés dans leurs onglets respectifs (forfaits détaillés).
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Services Catalog */}
            <TabsContent value="services" className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold">Catalogue de Services</h3>
                  <p className="text-sm text-slate-500">Services proposés sur le site</p>
                </div>
                <Button onClick={() => { setEditingService(null); setShowServiceModal(true); }} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Ajouter un service
                </Button>
              </div>
              
              {/* Services by category */}
              {SERVICE_CATEGORIES.map(category => {
                const categoryServices = services.filter(s => s.category === category.id);
                if (categoryServices.length === 0) return null;
                
                const Icon = category.icon;
                return (
                  <Card key={category.id}>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Icon className="w-4 h-4" />
                        {category.label}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="divide-y">
                        {categoryServices.map(service => (
                          <div key={service.id} className="py-3 flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{service.name}</span>
                                {!service.is_active && (
                                  <Badge variant="outline" className="text-xs text-slate-400">Inactif</Badge>
                                )}
                              </div>
                              {service.description && (
                                <p className="text-sm text-slate-500">{service.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-semibold">{service.base_price.toLocaleString('fr-FR')}€</p>
                                <p className="text-xs text-slate-500">
                                  {service.price_type === 'fixed' ? 'Prix fixe' : 
                                   service.price_type === 'hourly' ? 'Par heure' : 'Forfait'}
                                </p>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={() => handleEditService(service)}>
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteService(service.id)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {services.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun service</h3>
                    <p className="text-slate-500 mb-4">Ajoutez vos premiers services</p>
                    <Button onClick={() => setShowServiceModal(true)} variant="outline" className="gap-2">
                      <Plus className="w-4 h-4" />
                      Ajouter un service
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Contract Pricing - REPLACED with Hosting and Maintenance tabs */}
            {/* Hosting Plans Tab */}
            <TabsContent value="hosting" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Server className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle>Forfaits Hébergement</CardTitle>
                      <CardDescription>
                        Ces tarifs sont synchronisés avec le site et l'espace client
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {hostingPlans.map((plan, index) => (
                      <div 
                        key={plan.id}
                        className={cn(
                          "p-4 rounded-xl border-2 transition-all",
                          plan.popular 
                            ? "border-blue-500 bg-blue-50/50" 
                            : "border-slate-200 bg-white"
                        )}
                      >
                        {plan.popular && (
                          <Badge className="bg-blue-500 text-white mb-2">Populaire</Badge>
                        )}
                        <Input
                          value={plan.name}
                          onChange={(e) => updateHostingPlan(index, 'name', e.target.value)}
                          className="font-bold text-lg border-none bg-transparent p-0 h-auto focus-visible:ring-0 mb-2"
                        />
                        <div className="flex items-baseline gap-1 mb-3">
                          <Input
                            type="number"
                            value={plan.monthly_price}
                            onChange={(e) => updateHostingPlan(index, 'monthly_price', e.target.value)}
                            className="w-20 text-2xl font-bold border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                            style={{ color: theme.colors?.accent }}
                          />
                          <span className="text-slate-500">€/mois</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Stockage</span>
                            <Input
                              value={plan.storage}
                              onChange={(e) => updateHostingPlan(index, 'storage', e.target.value)}
                              className="w-24 text-right font-medium border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                            />
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Emails</span>
                            <Input
                              value={plan.emails}
                              onChange={(e) => updateHostingPlan(index, 'emails', e.target.value)}
                              className="w-24 text-right font-medium border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                            />
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Backup</span>
                            <Input
                              value={plan.backup}
                              onChange={(e) => updateHostingPlan(index, 'backup', e.target.value)}
                              className="w-28 text-right font-medium border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                            />
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Support</span>
                            <Input
                              value={plan.support}
                              onChange={(e) => updateHostingPlan(index, 'support', e.target.value)}
                              className="w-32 text-right font-medium border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                            />
                          </div>
                        </div>
                        <Input
                          value={plan.description}
                          onChange={(e) => updateHostingPlan(index, 'description', e.target.value)}
                          className="text-xs text-slate-500 mt-3 border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                          placeholder="Description..."
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Maintenance Plans Tab */}
            <TabsContent value="maintenance" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle>Forfaits Maintenance</CardTitle>
                      <CardDescription>
                        Ces tarifs sont synchronisés avec le site et l'espace client
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {maintenancePlans.map((plan, index) => (
                      <div 
                        key={plan.id}
                        className={cn(
                          "p-4 rounded-xl border-2 transition-all",
                          plan.popular 
                            ? "border-emerald-500 bg-emerald-50/50" 
                            : "border-slate-200 bg-white"
                        )}
                      >
                        {plan.popular && (
                          <Badge className="bg-emerald-500 text-white mb-2">Populaire</Badge>
                        )}
                        <Input
                          value={plan.name}
                          onChange={(e) => updateMaintenancePlan(index, 'name', e.target.value)}
                          className="font-bold text-lg border-none bg-transparent p-0 h-auto focus-visible:ring-0 mb-2"
                        />
                        <div className="flex items-baseline gap-1 mb-3">
                          <Input
                            type="number"
                            value={plan.monthly_price}
                            onChange={(e) => updateMaintenancePlan(index, 'monthly_price', e.target.value)}
                            className="w-20 text-2xl font-bold border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                            style={{ color: theme.colors?.accent }}
                          />
                          <span className="text-slate-500">€/mois</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Heures incluses</span>
                            <div className="flex items-center gap-1">
                              {plan.hours_included === -1 ? (
                                <span className="font-medium">Illimitées</span>
                              ) : (
                                <>
                                  <Input
                                    type="number"
                                    value={plan.hours_included}
                                    onChange={(e) => updateMaintenancePlan(index, 'hours_included', e.target.value)}
                                    className="w-12 text-right font-medium border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                                  />
                                  <span className="font-medium">h</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Délai réponse</span>
                            <Input
                              value={plan.response_time}
                              onChange={(e) => updateMaintenancePlan(index, 'response_time', e.target.value)}
                              className="w-16 text-right font-medium border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                            />
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Mises à jour</span>
                            <Input
                              value={plan.updates}
                              onChange={(e) => updateMaintenancePlan(index, 'updates', e.target.value)}
                              className="w-28 text-right font-medium border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                            />
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Rapports</span>
                            <Input
                              value={plan.reports}
                              onChange={(e) => updateMaintenancePlan(index, 'reports', e.target.value)}
                              className="w-28 text-right font-medium border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                            />
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-500">Monitoring</span>
                            <Badge variant="outline" className={cn(
                              "text-xs",
                              plan.monitoring ? "bg-green-50 text-green-700" : "bg-slate-50 text-slate-500"
                            )}>
                              {plan.monitoring ? 'Inclus' : 'Non inclus'}
                            </Badge>
                          </div>
                        </div>
                        <Input
                          value={plan.description}
                          onChange={(e) => updateMaintenancePlan(index, 'description', e.target.value)}
                          className="text-xs text-slate-500 mt-3 border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                          placeholder="Description..."
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* Service Modal */}
      <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingService ? 'Modifier le service' : 'Nouveau service'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom du service *</Label>
              <Input
                value={serviceForm.name}
                onChange={(e) => setServiceForm({...serviceForm, name: e.target.value})}
                placeholder="Ex: Création de logo"
              />
            </div>
            
            <div>
              <Label>Catégorie</Label>
              <Select value={serviceForm.category} onValueChange={(val) => setServiceForm({...serviceForm, category: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={serviceForm.description}
                onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                placeholder="Description du service..."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type de tarification</Label>
                <Select value={serviceForm.price_type} onValueChange={(val) => setServiceForm({...serviceForm, price_type: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Prix fixe</SelectItem>
                    <SelectItem value="hourly">À l'heure</SelectItem>
                    <SelectItem value="package">Forfait</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Prix de base (€ HT) *</Label>
                <Input
                  type="number"
                  value={serviceForm.base_price}
                  onChange={(e) => setServiceForm({...serviceForm, base_price: e.target.value})}
                  placeholder="0"
                />
              </div>
            </div>
            
            {serviceForm.price_type !== 'fixed' && (
              <div>
                <Label>Heures estimées</Label>
                <Input
                  type="number"
                  value={serviceForm.estimated_hours}
                  onChange={(e) => setServiceForm({...serviceForm, estimated_hours: e.target.value})}
                  placeholder="0"
                />
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <Label>Service actif</Label>
              <Switch
                checked={serviceForm.is_active}
                onCheckedChange={(checked) => setServiceForm({...serviceForm, is_active: checked})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveService}>
              {editingService ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingSettingsPage;
